import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogTrigger } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { removeMember } from '~/api/projects2';
import { Action } from '~/authorizations';
import Can from '~/authorizations/Can';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Member } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { useGetProject } from '~/hooks/useGetProject';
import type { ProjectDetailsParams } from '..';

interface Props {
  member: Member;
}

export default function RemoveMember({ member }: Props) {
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const projectRequest = useGetProject({ projectId }, { refetchOnWindowFocus: false });
  const project = projectRequest.data;
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const isMemberPM = project?.projectManager.id?.toString() === member.user.id.toString();

  return (
    <Can action={Action.RemoveProjectMember}>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            variant={!isMemberPM ? 'danger' : 'secondary'}
            disabled={isMemberPM}
            tooltip={`${member.user.name || member.user.email} is the current PM. Please reassign before removing member`}
            data-testid="project-team-remove-member-trigger"
          >
            Remove from Project
          </Button>
        </DialogTrigger>
        <DialogContent
          title="Remove Member"
          className="z-[110] w-auto min-w-min max-w-[75vw]"
          overlayClassName="z-[100]"
          data-testid="project-team-remove-member-dialog"
        >
          <DialogBody className="text-sm">
            Are you sure you want to remove {member.user.name} from this project?
          </DialogBody>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} data-testid="project-team-remove-member-no">
              No
            </Button>
            <Button
              variant="danger"
              data-testid="project-team-remove-member-yes"
              onClick={async () => {
                await removeMember(projectId, member.user.id);
                queryClient.invalidateQueries({ queryKey: ['project', { projectId }] });
                Notify.info({
                  message: `${member.user.name} has been removed from the project.`,
                });

                capturePosthogEvent(POSTHOG_EVENTS.projectTeamMemberRemoved, {
                  project_id: projectId,
                  organization_id: project?.organization || undefined,
                  removed_member_id: member.user.id,
                });

                setOpen(false);
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Can>
  );
}
